import { Box } from '@mui/material'
import { FC, ReactNode, useRef, useState } from 'react'
import { useMinimalsTheme } from '../../../hooks'

export interface ResizableCardProps {
  initialWidth?: number
  minWidth?: number
  maxWidth?: number
  children: ReactNode
}

export const ResizableCard: FC<ResizableCardProps> = ({
  initialWidth = 300,
  minWidth = 200,
  maxWidth = 600,
  children,
}) => {
  const [width, setWidth] = useState<number>(initialWidth)
  const [isDragging, setIsDragging] = useState<boolean>(false)
  const draggingRef = useRef<boolean>(false)
  const theme = useMinimalsTheme()

  const handleMouseMove = (e: globalThis.MouseEvent) => {
    if (!draggingRef.current) return

    setWidth(prev => {
      const next = prev + e.movementX
      return Math.max(minWidth, Math.min(maxWidth, next))
    })
  }

  const startDrag = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault()
    draggingRef.current = true
    setIsDragging(true)

    document.body.style.userSelect = 'none'
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', stopDrag)
  }

  const stopDrag = () => {
    draggingRef.current = false
    setIsDragging(false)

    document.body.style.userSelect = ''
    document.removeEventListener('mousemove', handleMouseMove)
    document.removeEventListener('mouseup', stopDrag)
  }

  return (
    <Box
      sx={{
        position: 'relative',
        width,
        minWidth,
        maxWidth,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        flexShrink: 0,
      }}
    >
      <Box sx={{ flex: 1 }}>{children}</Box>

      <Box
        onMouseDown={startDrag}
        sx={{
          position: 'absolute',
          backgroundColor: isDragging ? theme.palette.secondary.light : 'none',
          top: 0,
          right: 0,
          width: 8,
          height: '100%',
          cursor: 'col-resize',
          zIndex: 100,
          '&:hover': { backgroundColor: theme.palette.secondary.light },
        }}
      />
    </Box>
  )
}
